import { useDialog } from "@opencode-ai/ui/context/dialog"
import { useTheme } from "@opencode-ai/ui/theme/context"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import { makeEventListener } from "@solid-primitives/event-listener"
import { useLocation, useNavigate } from "@solidjs/router"
import { createEffect, createMemo, createSignal, For, on, Show, Suspense, untrack, type JSX, type ParentProps } from "solid-js"
import { createStore } from "solid-js/store"
import { DialogDatoxActivityLogs } from "@/components/datox-activity-logs-dialog"
import { DialogDatoxUsage } from "@/components/datox-usage-dialog"
import { useSettingsCommand } from "@/components/settings-dialog"
import type { TitlebarUpdate } from "@/components/titlebar"
import { applyPath, backPath, forwardPath, type TitlebarHistory } from "@/components/titlebar-history"
import { useCommand } from "@/context/command"
import { useLanguage } from "@/context/language"
import { usePlatform } from "@/context/platform"
import { datoxV2Design, listCompanies, type DatoxCompany } from "@/datox/api"
import { activeCompanyID, localDevHeaders, useDatoxAuth } from "@/datox/auth-ui"
import { switchCompany } from "@/datox/local-auth"
import { useMenu } from "@/datox/v2/menu"
import { isCurrent, openFor, RAIL_GROUPS, RAIL_MAIN, railUser, readGroups, recentPath, recentReports, toggleGroup, writeGroups, type RailGroup, type RailItem } from "@/datox/v2/rail"
import { TablerIcon } from "@/datox/v2/tabler-icons"
import { DialogDatoxLogout } from "@/pages/home/datox-project-dialogs"
import { ownsTitle } from "@/datox/v2/brand-head"
import { PageSkeleton } from "@/datox/v2/page-skeleton"
import { switchDesign } from "@/datox/v2/design-flag"
import { darkSwitch } from "@/datox/v2/theme-switch"
import "@/datox/v2/tokens.css"
import "@/datox/v2/design-switch.css"

const TITLEBAR_HEIGHT = 36
const MAC_TRAFFIC_LIGHTS_WIDTH = 84
const WINDOWS_CONTROLS_WIDTH = 138
const RAIL_WIDTH = 48
const SIDEBAR_WIDTH = 220
// Below this the sidebar starts (and falls back to) the icon rail, whatever was remembered.
const WIDE = "(min-width: 1100px)"

// The new Lesha Bank app shell: a sidebar after Stripe's (DESIGN-LOG §57, mock 67 variant A) that collapses to the 48px
// icon rail. The bank's mark and the company on top (a company menu when there are several), the four everyday places,
// Recent (the last factsheets opened), then "Workspace tools" in three groups that open in place; the account at the
// foot. What it lists and remembers is datox/v2/rail.ts. It stands in for Titlebar + DatoxNavigationShell, so it also
// keeps their commands (back / forward / home, settings), the titlebar mount hosts pages portal into, the updater and
// desktop chrome.
export function DatoxV2Shell(props: ParentProps<{ update?: TitlebarUpdate }>) {
  const language = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const platform = usePlatform()
  const command = useCommand()
  const auth = useDatoxAuth()
  const t = language.t
  const current = (item: RailItem) => isCurrent(item, location.pathname)
  const go = (path: string) => navigate(path)
  // A plain click routes in the app; with a modifier (a new tab or window) the link is the browser's.
  const follow = (path: string) => (event: MouseEvent) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    go(path)
  }
  // The report editor (/reports/:projectID/:workspaceID) is a focused full-screen workspace (Artyom: «два меню и правда
  // выглядят не очень… в редакторе можно основное прятать»): the shell drops its rail there. The editor's own tool rail
  // carries the Lesha mark (to /reports) and this avatar menu, and its ← returns to the list with its filters.
  const focus = () => /^\/reports\/[^/]+\/[^/]+\/?$/.test(location.pathname)
  // The tab title on every rail page (DEMO-SWEEP 6.3): "<rail label> — Lesha Bank". Pages that name themselves own it.
  // Set after the route change settles, so a leaving page's cleanup (which restores its previous title) can't win.
  createEffect(on(() => location.pathname, (pathname) => {
    if (ownsTitle(pathname)) return
    queueMicrotask(() => {
      if (location.pathname !== pathname) return
      const item = [...RAIL_MAIN, ...RAIL_GROUPS.flatMap((group) => group.items)].find(current)
      document.title = item ? t("datox.v2.title.page", { page: t(item.key) }) : t("datox.v2.title.app")
    })
  }))

  // Expanded or collapsed, and the open groups, remembered per user in this browser; narrow windows start (and fall
  // back to) the rail.
  const user = () => railUser(auth.state.accountEmail)
  const storageKey = () => `dx-v2-sidebar:${user()}`
  const remembered = () => {
    try {
      return localStorage.getItem(storageKey())
    } catch {
      return null
    }
  }
  const wide = () => typeof window.matchMedia !== "function" || window.matchMedia(WIDE).matches
  const [menu, setMenu] = createStore({
    expanded: wide() && remembered() !== "collapsed",
    groups: [] as string[],
    // Until the list is in, the head shows the mark alone (never a placeholder company).
    companies: undefined as DatoxCompany[] | undefined,
  })
  const expanded = () => menu.expanded
  const setExpanded = (value: boolean) => setMenu("expanded", value)
  // The account resolves after the first paint: its own remembered state wins once it is known.
  createEffect(on(storageKey, () => setExpanded(wide() && remembered() !== "collapsed"), { defer: true }))
  if (typeof window.matchMedia === "function") {
    const query = window.matchMedia(WIDE)
    makeEventListener(query, "change", () => setExpanded(query.matches && remembered() !== "collapsed"))
  }
  const toggleSidebar = () => {
    const next = !expanded()
    setExpanded(next)
    try {
      localStorage.setItem(storageKey(), next ? "expanded" : "collapsed")
    } catch {}
  }
  const railWidth = () => (expanded() ? SIDEBAR_WIDTH : RAIL_WIDTH)
  const setGroups = (open: string[]) => {
    setMenu("groups", open)
    writeGroups(user(), open)
  }
  createEffect(on(user, (value) => setMenu("groups", openFor(readGroups(value), location.pathname))))
  createEffect(on(() => location.pathname, (pathname) => {
    const next = openFor(menu.groups, pathname)
    if (next !== menu.groups) setGroups(next)
  }, { defer: true }))
  const recent = createMemo(() => recentReports(user()))

  // The companies, read once for the head and the account row (in the report editor DatoxV2Profile reads its own). A
  // plain promise rather than a resource: a resource read here would suspend (and on error, throw through) the shell.
  let asked = false
  createEffect(() => {
    if (asked || !auth.authenticated()) return
    asked = true
    listCompanies().then((list) => setMenu("companies", list)).catch(() => setMenu("companies", []))
  })
  const companies = () => (menu.companies ?? []).filter((item) => item.status === "active")
  const companyName = () => (menu.companies ? companyTitle(language, menu.companies.find((item) => item.id === activeCompanyID())) : "")
  const homeLabel = () => (companyName() ? t("datox.v2.nav.company.home", { company: companyName() }) : t("lesha.logo.alt"))
  const switchLabel = () => t("datox.v2.nav.company.switch", { company: companyName() })

  // The company menu and, on the 48px rail, a group's tools: one at a time, beside the rail while it is collapsed.
  const menus = useMenu<string>({ class: "dx-v2-rail-pop", left: () => true, side: () => !expanded() })
  const popped = (kind: string) => menus.menu()?.kind === kind
  const popGroup = () => RAIL_GROUPS.find((group) => popped(group.id))
  // A tool picked with the keyboard puts focus back on its group; after a click the new page has it.
  const pick = (path: string) => (event: MouseEvent) => {
    menus.close(event.detail === 0)
    follow(path)(event)
  }

  // Back / forward over the app's own path history, and mod+b home, as Titlebar registered them.
  const [history, setHistory] = createStore({ stack: [] as string[], index: 0, action: undefined as "back" | "forward" | undefined })
  createEffect(() => {
    const current = `${location.pathname}${location.search}${location.hash}`
    untrack(() => {
      const next = applyPath(history, current)
      if (next !== history) setHistory(next)
    })
  })
  const step = (next?: { state: TitlebarHistory; to: string }) => {
    if (!next) return
    setHistory(next.state)
    navigate(next.to)
  }
  command.register(() => [
    { id: "common.goBack", title: language.t("common.goBack"), category: language.t("command.category.view"), keybind: "mod+[", onSelect: () => step(backPath(history)) },
    { id: "common.goForward", title: language.t("common.goForward"), category: language.t("command.category.view"), keybind: "mod+]", onSelect: () => step(forwardPath(history)) },
  ])
  command.register("titlebar-home", () => [
    { id: "home.toggle", title: language.t("home.title"), category: language.t("command.category.view"), keybind: "mod+b", hidden: true, onSelect: () => go("/reports") },
  ])

  // Desktop chrome: the bar is the drag region; macOS traffic lights sit over the rail's top and the bar's start,
  // Windows caption buttons over the bar's end.
  const desktop = platform.platform === "desktop"
  const mac = desktop && platform.os === "macos"
  const windows = desktop && platform.os === "windows"
  const zoom = () => platform.webviewZoom?.() ?? 1
  const lights = () => mac && !platform.windowFullscreen?.()
  const updateVersion = () => props.update?.version()
  const installing = () => props.update?.installing() ?? false

  // One row for both widths: a 20px icon and the label; the 48px rail shows the icon and names it in a tooltip.
  const row = (item: RailItem) => (
    <TooltipV2 placement="right" value={t(item.key)} inactive={expanded()}>
      <a class="dx-v2-rail-row" href={item.path} aria-label={t(item.key)} aria-current={current(item) ? "page" : undefined} onClick={follow(item.path)}>
        <TablerIcon name={item.icon} size={20} stroke-width="1.5" />
        <span class="dx-v2-rail-label">{t(item.key)}</span>
      </a>
    </TooltipV2>
  )
  // A Workspace tools group: in the sidebar it opens in place (its tools as indented text rows), on the rail its icon
  // opens a menu of them. It is blue while it holds the current page out of sight.
  const tools = (group: RailGroup) => {
    const open = () => menu.groups.includes(group.id)
    return (
      <>
        <TooltipV2 placement="right" value={t(group.key)} inactive={expanded()}>
          <button
            type="button"
            class="dx-v2-rail-row"
            aria-label={t(group.key)}
            aria-haspopup={expanded() ? undefined : "menu"}
            aria-expanded={expanded() ? open() : popped(group.id)}
            data-holds-current={(group.items.some(current) && !(expanded() && open())) || undefined}
            onClick={(event) => (expanded() ? setGroups(toggleGroup([...menu.groups], group.id)) : menus.open(group.id, event.currentTarget))}
          >
            <TablerIcon name={group.icon} size={20} stroke-width="1.5" />
            <span class="dx-v2-rail-label">{t(group.key)}</span>
            <TablerIcon class="dx-v2-rail-chevron" name="chevron-down" size={16} stroke-width="1.5" />
          </button>
        </TooltipV2>
        <Show when={expanded() && open()}>
          <div class="dx-v2-rail-subs" role="group" aria-label={t(group.key)}>
            <For each={group.items}>
              {(item) => (
                <a class="dx-v2-rail-sub" href={item.path} aria-current={current(item) ? "page" : undefined} onClick={follow(item.path)}>
                  {t(item.key)}
                </a>
              )}
            </For>
          </div>
        </Show>
      </>
    )
  }
  const face = () => (
    <>
      <span class="dx-v2-rail-tile">
        <img src="/lesha-bank-mark.png" alt="" />
      </span>
      <span class="dx-v2-rail-company-name">{companyName()}</span>
    </>
  )

  const company = () => (
    <Show
      when={companies().length > 1}
      fallback={
        <TooltipV2 placement="right" value={homeLabel()} inactive={expanded()}>
          <a class="dx-v2-rail-company" href="/reports" aria-label={homeLabel()} onClick={follow("/reports")}>
            {face()}
          </a>
        </TooltipV2>
      }
    >
      <TooltipV2 placement="right" value={switchLabel()} inactive={expanded()}>
        <button
          type="button"
          class="dx-v2-rail-company"
          aria-label={switchLabel()}
          aria-haspopup="menu"
          aria-expanded={popped("company")}
          onClick={(event) => menus.open("company", event.currentTarget)}
        >
          {face()}
          <TablerIcon class="dx-v2-rail-company-chevron" name="chevron-down" size={16} stroke-width="1.5" />
        </button>
      </TooltipV2>
    </Show>
  )
  const nav = () => (
    <div class="dx-v2-rail-nav">
      <For each={RAIL_MAIN}>{row}</For>
      {/* Recent: the last three factsheets opened in the editor, newest first (datox/v2/rail.ts). None, no section. */}
      <Show when={expanded() && recent().length}>
        <div class="dx-v2-rail-section" role="group" aria-labelledby="dx-v2-rail-recent">
          <span id="dx-v2-rail-recent" class="dx-v2-rail-caption">{t("datox.v2.nav.recent")}</span>
          <For each={recent()}>
            {(entry) => (
              <a class="dx-v2-rail-row" data-recent href={recentPath(entry)} onClick={follow(recentPath(entry))}>
                <TablerIcon name="history" size={20} stroke-width="1.5" />
                <span class="dx-v2-rail-label">{entry.name}</span>
              </a>
            )}
          </For>
        </div>
      </Show>
      <div class="dx-v2-rail-section" role="group" aria-labelledby="dx-v2-rail-tools">
        <span id="dx-v2-rail-tools" class="dx-v2-rail-caption">{t("datox.designer.workspaceTools")}</span>
        <For each={RAIL_GROUPS}>{tools}</For>
      </div>
    </div>
  )
  // TooltipV2 sets itself up once, as it mounts: one mounted in the sidebar (inactive, no trigger) would not step aside
  // for an open menu after a collapse. So the rail's parts mount again when the width changes; the toggle stays put.
  const perWidth = (part: () => JSX.Element) => (
    <Show when={expanded()} fallback={part()}>
      {part()}
    </Show>
  )

  return (
    <div class="dx-v2-shell" data-focus={focus() || undefined}>
      <Show when={!focus()}>
        <nav
          class="dx-v2-rail"
          data-expanded={expanded() || undefined}
          aria-label={t("datox.v2.nav")}
          style={lights() ? { "padding-top": `${TITLEBAR_HEIGHT / zoom()}px` } : undefined}
          data-tauri-drag-region={desktop || undefined}
        >
          {/* The bank's mark and the company (Stripe's account row): a link to Reports, or with several companies a
              menu that switches between them the way Settings > Admin does. */}
          <div class="dx-v2-rail-head">
            {perWidth(company)}
            <TooltipV2 placement="right" value={t(expanded() ? "datox.v2.nav.collapse" : "datox.v2.nav.expand")}>
              <button type="button" class="dx-v2-rail-toggle" aria-label={t(expanded() ? "datox.v2.nav.collapse" : "datox.v2.nav.expand")} aria-expanded={expanded()} onClick={toggleSidebar}>
                <TablerIcon name="layout-sidebar-left-collapse" size={18} stroke-width="1.5" />
              </button>
            </TooltipV2>
          </div>
          {perWidth(nav)}
          <menus.Panel label={popGroup() ? t(popGroup()!.key) : t("datox.v2.nav.companies")}>
            {() => (
              <Show
                when={popGroup()}
                fallback={
                  <div class="dx-v2-menu-section">
                    <div class="dx-v2-menu-caption dx-v2-rail-pop-caption" aria-hidden="true">{t("datox.v2.nav.companies")}</div>
                    <For each={companies()}>
                      {(item) => {
                        const active = item.id === activeCompanyID()
                        return (
                          <button type="button" role="menuitemradio" class="dx-v2-menu-item" aria-checked={active} onClick={() => (active ? menus.close(true) : switchCompany(item))}>
                            <span class="dx-v2-rail-pop-tile" aria-hidden="true">
                              {active ? <img src="/lesha-bank-mark.png" alt="" /> : companyTitle(language, item).slice(0, 1).toUpperCase()}
                            </span>
                            <span class="dx-v2-rail-pop-name">{companyTitle(language, item)}</span>
                            <Show when={active}>
                              <TablerIcon class="dx-v2-rail-pop-check" name="check" size={16} />
                            </Show>
                          </button>
                        )
                      }}
                    </For>
                  </div>
                }
              >
                {(group) => (
                  <div class="dx-v2-menu-section">
                    <div class="dx-v2-menu-caption dx-v2-rail-pop-caption" aria-hidden="true">{t(group().key)}</div>
                    <For each={group().items}>
                      {(item) => (
                        <a role="menuitem" class="dx-v2-menu-item" href={item.path} aria-current={current(item) ? "page" : undefined} onClick={pick(item.path)}>
                          <TablerIcon name={item.icon} size={16} stroke-width="1.5" />
                          {t(item.key)}
                        </a>
                      )}
                    </For>
                  </div>
                )}
              </Show>
            )}
          </menus.Panel>
          <span class="dx-v2-rail-spring" />
          {perWidth(() => <DatoxV2Profile expanded={expanded()} companies={() => menu.companies} />)}
        </nav>
      </Show>
      <div class="dx-v2-main">
        <header
          class="dx-v2-topbar"
          data-desktop={desktop || undefined}
          data-tauri-drag-region={desktop || undefined}
          style={{
            "min-height": desktop ? `${TITLEBAR_HEIGHT / zoom()}px` : undefined,
            "padding-left": lights() ? `${Math.max(0, MAC_TRAFFIC_LIGHTS_WIDTH - (focus() ? 0 : railWidth())) / zoom()}px` : undefined,
            "padding-right": windows ? `${WINDOWS_CONTROLS_WIDTH / Math.max(zoom(), 1)}px` : undefined,
          }}
        >
          <span />
          <div id="opencode-titlebar-center" class="dx-v2-topbar-slot dx-v2-topbar-slot--center" />
          <div class="dx-v2-topbar-end">
            <Show when={updateVersion() !== undefined || installing()}>
              <button
                type="button"
                class="dx-v2-update"
                onClick={() => props.update?.install()}
                disabled={installing()}
                aria-busy={installing()}
                aria-label={language.t("toast.update.action.installRestart")}
                title={updateVersion() ? language.t("titlebar.updateVersion", { version: updateVersion()! }) : undefined}
              >
                <TablerIcon name="download" size={12} />
                {language.t("titlebar.update")}
              </button>
            </Show>
            <div id="opencode-titlebar-right" class="dx-v2-topbar-slot" />
          </div>
        </header>
        {/* A fresh visit waits for the page's code: its skeleton meanwhile, the rail already drawn (DESIGN-LOG §63). */}
        <Suspense fallback={<PageSkeleton path={location.pathname} search={location.search} />}>{props.children}</Suspense>
      </div>
    </div>
  )
}

// The rail avatar opens the account menu: the items and actions of Titlebar's DatoxTitlebarProfile, drawn as a V2 menu.
// Also mounted at the foot of the report editor's tool rail, where the shell rail is hidden. In the expanded sidebar it is
// the account row (initials, name, company) and the menu opens above it.
export function DatoxV2Profile(props: { expanded?: boolean; companies?: () => DatoxCompany[] | undefined }) {
  const auth = useDatoxAuth()
  const language = useLanguage()
  const dialog = useDialog()
  const theme = useTheme()
  const openSettings = useSettingsCommand()
  const [open, setOpen] = createSignal(false)
  // Dark theme: the base app's colour scheme (Settings keeps Light, Dark and System); the menu stays open to show it.
  const dark = () => darkSwitch(theme.mode())
  // In the shell the rail's own read of the companies; in the report editor this menu reads them. A plain promise
  // rather than a resource: a resource read here would suspend (and on error, throw through) the whole shell.
  const [own, setOwn] = createSignal<DatoxCompany[]>([])
  let asked = false
  createEffect(() => {
    if (props.companies || asked || !auth.authenticated()) return
    asked = true
    listCompanies().then(setOwn).catch(() => {})
  })
  let root: HTMLDivElement | undefined
  let trigger: HTMLButtonElement | undefined

  const userLabel = (): string =>
    auth.state.accountName || auth.state.accountEmail || localDevHeaders()["X-User-Email"] || localDevHeaders()["X-User-Id"] || ""
  const companyLabel = () => companyTitle(language, (props.companies?.() ?? own()).find((item) => item.id === activeCompanyID()))
  const initials = () =>
    userLabel()
      .split(/[\s.@]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part[0]!.toUpperCase())
      .join("") || "U"
  const run = (action: () => void) => () => {
    setOpen(false)
    action()
  }
  const logout = () =>
    dialog.show(() => (
      <DialogDatoxLogout
        user={userLabel()}
        onLogout={() => {
          localStorage.removeItem("activeCompanyId")
          localStorage.removeItem("datox.agent.companyId")
          localStorage.removeItem("datox.agent.userId")
          localStorage.removeItem("datox.agent.userEmail")
          sessionStorage.clear()
          window.location.reload()
        }}
      />
    ))

  makeEventListener(document, "pointerdown", (event) => {
    if (open() && root && !root.contains(event.target as Node)) setOpen(false)
  })
  makeEventListener(document, "keydown", (event) => {
    if (!open() || event.key !== "Escape") return
    event.preventDefault()
    setOpen(false)
    trigger?.focus()
  })

  return (
    <div ref={root} class="dx-v2-rail-menu" data-expanded={props.expanded || undefined}>
      <TooltipV2 placement="right" value={userLabel()} inactive={props.expanded}>
        <button
          ref={trigger}
          type="button"
          class="dx-v2-account"
          aria-label={userLabel()}
          aria-haspopup="menu"
          aria-expanded={open()}
          onClick={() => setOpen(!open())}
        >
          <span class="dx-v2-rail-avatar">{initials()}</span>
          <Show when={props.expanded}>
            <span class="dx-v2-account-text">
              <b>{userLabel()}</b>
              <span>{companyLabel()}</span>
            </span>
            <TablerIcon class="dx-v2-account-more" name="dots" size={16} stroke-width="1.5" />
          </Show>
        </button>
      </TooltipV2>
      <Show when={open()}>
        <div class="dx-v2-menu" role="menu" aria-label={userLabel()}>
          <div class="dx-v2-menu-section">
            <div class="dx-v2-menu-head">
              <b>{userLabel()}</b>
              <span class="dx-v2-menu-caption">{companyLabel()}</span>
            </div>
          </div>
          <div class="dx-v2-menu-section">
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(() => dialog.show(() => <DialogDatoxActivityLogs />))}>
              <TablerIcon name="activity" size={16} />
              {language.t("datox.activity.menu")}
            </button>
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(() => dialog.show(() => <DialogDatoxUsage />))}>
              <TablerIcon name="chart-bar" size={16} />
              {language.t("datox.usage.menu")}
            </button>
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(openSettings)}>
              <TablerIcon name="settings" size={16} />
              {language.t("sidebar.settings")}
            </button>
          </div>
          {/* The design switch (datox/v2/design-flag.ts): off reloads into Bobur's classic app. Under it, Dark theme
              (datox/v2/theme-switch.ts), as Compliance V2 has it. */}
          <div class="dx-v2-menu-section">
            <button type="button" role="menuitemcheckbox" aria-checked={datoxV2Design()} class="dx-v2-menu-item" onClick={run(() => switchDesign(!datoxV2Design()))}>
              {language.t("datox.v2.design.toggle")}
              <span class="dx-v2-design-switch" data-on={datoxV2Design() || undefined} aria-hidden="true" />
            </button>
            <button type="button" role="menuitemcheckbox" aria-checked={dark().on} class="dx-v2-menu-item" onClick={() => theme.setColorScheme(dark().next)}>
              {language.t("datox.v2.theme.dark")}
              <span class="dx-v2-design-switch" data-on={dark().on || undefined} aria-hidden="true" />
            </button>
          </div>
          <div class="dx-v2-menu-section">
            <button type="button" role="menuitem" class="dx-v2-menu-item" onClick={run(logout)}>
              <TablerIcon name="logout" size={16} />
              {language.t("datox.profile.logout")}
            </button>
          </div>
        </div>
      </Show>
    </div>
  )
}

// The dev database's seed company shows as the product's default name.
function companyTitle(language: ReturnType<typeof useLanguage>, company?: DatoxCompany) {
  if (!company || company.name === "Local Development Company") return language.t("datox.company.default")
  return company.name
}
